import { LOT_EVENT_LABELS, LOT_EVENT_TYPES, type LotEventType } from "./enums";

/**
 * Журнал действий (раздел 16.6): таблица audit_log — кто, что, над чем, когда, payload.
 * События по лотам живут в lot_events; вкладка «Журнал» показывает объединённую ленту.
 */
export const AUDIT_ACTIONS = [
  "settings.update",
  "company.create",
  "company.update",
  "contact.unlink",
  "invite.create",
  "project.create",
  "project.update",
  "vehicle_type.create",
  "vehicle_type.update",
  "user.create",
  "user.update",
  "user.password_reset",
  "user.unlink_telegram",
  "lot_template.create",
  "lot_template.update",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_ENTITY_TYPES = ["setting", "company", "contact", "invite", "project", "vehicle_type", "user", "lot_template"] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  "settings.update": "Изменена настройка",
  "company.create": "Добавлена компания",
  "company.update": "Изменена компания",
  "contact.unlink": "Отвязан Telegram менеджера",
  "invite.create": "Выпущена ссылка-приглашение",
  "project.create": "Добавлен проект",
  "project.update": "Изменён проект",
  "vehicle_type.create": "Добавлен тип транспорта",
  "vehicle_type.update": "Изменён тип транспорта",
  "user.create": "Создан пользователь",
  "user.update": "Изменён пользователь",
  "user.password_reset": "Сброшен пароль пользователя",
  "user.unlink_telegram": "Отвязан Telegram сотрудника",
  "lot_template.create": "Создан шаблон заявки",
  "lot_template.update": "Изменён шаблон заявки",
};

export const AUDIT_ENTITY_LABELS: Record<AuditEntityType, string> = {
  setting: "Настройка",
  company: "Компания",
  contact: "Контакт",
  invite: "Приглашение",
  project: "Проект",
  vehicle_type: "Тип транспорта",
  user: "Пользователь",
  lot_template: "Шаблон заявки",
};

/** Тип записи в объединённой ленте: действие над справочником/настройкой или событие по лоту. */
export type JournalEntryType = AuditAction | LotEventType;

/** Все типы для фильтра «Тип» во вкладке «Журнал»: сначала события лотов, затем действия. */
export const JOURNAL_TYPES: readonly JournalEntryType[] = [...LOT_EVENT_TYPES, ...AUDIT_ACTIONS];

export const journalTypeLabel = (type: string): string =>
  (LOT_EVENT_LABELS as Record<string, string>)[type] ?? (AUDIT_ACTION_LABELS as Record<string, string>)[type] ?? type;

/** Строка GET /api/admin/audit: UNION audit_log + lot_events. Даты — ISO-строки. */
export type JournalEntry = {
  id: string;
  source: "audit" | "lot";
  type: string;
  createdAt: string;
  actorUserId: string | null;
  actorName: string | null;
  /** Для событий лота от перевозчика (ставка, сообщение о доставке). */
  actorCompanyName: string | null;
  entityType: string | null;
  entityId: string | null;
  lotId: string | null;
  lotNumber: string | null;
  payload: Record<string, unknown>;
};

export type JournalQuery = {
  from?: string;
  to?: string;
  userId?: string;
  type?: string;
  lotId?: string;
  /** Номер заявки (TR-00125), поиск без учёта регистра. */
  lot?: string;
  page?: number;
};

export const JOURNAL_PAGE_SIZE = 50;

export type JournalPage = {
  items: JournalEntry[];
  page: number;
  pageSize: number;
  total: number;
};
